import {
  DutyLogDataService,
  DutyLogEntry,
  DutyLogCreateRequest,
  DutyLogApiResponse,
  DutyLogFetchRequest,
  DataRequest,
  DataResponse,
  FilterDefinition,
  ExportFormat,
  DutyLogServiceError,
  SortDefinition
} from '../types/dutylog';
import { QueryParams, QueryResult, SearchParam, SortParam, FilterParam, EntityServiceAdapter } from '../types/baseExplorerConfig';
import { logger } from '../utils/logger';
import { getBackendUrl } from './configService';
import { generateLogbookPdf, downloadPdf, LogbookEntry, DEFAULT_REPORT_FOOTER } from './pdfReportService';

const log = logger('DutyLogService');

// TODO: AI_CHECKPOINT - DutyLogService implementation for Step 4 CRUD operations

/**
 * Service class for DutyLog API operations
 * Implements create and read operations (no edit/delete per business rules)
 * Tracks test entry IDs for cleanup purposes
 * 
 * ⚠️  CRITICAL SAFETY NOTE: This service connects to LIVE FIRE OPERATIONS API
 * All entries created will appear in active emergency response systems
 * Mock mode should be used for testing to avoid interfering with real operations
 */
export class DutyLogService implements DutyLogDataService {
  /**
   * Update an existing duty log entry (not supported per business rules)
   */
  async updateEntry(entry: any): Promise<DutyLogEntry> {
    throw this.createServiceError('API_ERROR', 'Update operation is not supported for DutyLog entries');
  }

  /**
   * Delete a duty log entry (not supported per business rules)
   */
  async deleteEntry(id: string): Promise<void> {
    throw this.createServiceError('API_ERROR', 'Delete operation is not supported for DutyLog entries');
  }
  private baseUrl: string;
  private testEntryIds: Set<string> = new Set(); // Track test entries for cleanup

  constructor(baseUrl: string = 'http://localhost:3400/dutylog') {
    this.baseUrl = baseUrl;

    // Load existing test IDs from localStorage if available
    this.loadTestEntryIds();
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = localStorage.getItem('access_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async createEntry(entry: DutyLogCreateRequest): Promise<DutyLogEntry> {
    log.debug('🚨 CREATEENTRY FUNCTION CALLED', { baseUrl: this.baseUrl });
    try {
      log.debug('✅ FRONTEND PAYLOAD LOG: The following payload is being sent to the backend:', JSON.stringify(entry, null, 2));
      log.debug('🚨 Creating duty log entry (production API) - PUT to:', this.baseUrl);
      const response = await fetch(this.baseUrl, {
        method: 'PUT',
        credentials: 'include',
        headers: this.getHeaders(),
        body: JSON.stringify(entry)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const createdEntry = await response.json();
      log.debug('✅ DUTY LOG CREATED:', createdEntry._id);

      // Track test entries for cleanup
      if (createdEntry.body?.startsWith('TEST') && createdEntry._id) {
        this.testEntryIds.add(createdEntry._id);
        this.saveTestEntryIds();
        log.debug(`🧪 TEST ENTRY CREATED: ID=${createdEntry._id}`, {
          user: entry.logUser,
          class: entry.logClass,
          context: entry.logContext,
          preview: createdEntry.body?.substring(0, 50) + '...'
        });
      }

      return createdEntry;
    } catch (error) {
      log.error('Error creating duty log entry:', error);
      throw this.createServiceError('API_ERROR', 'Failed to create duty log entry', error);
    }
  }

  /**
   * Get total count of duty log records, optionally filtered
   * Since /count endpoint only supports GET (unfiltered), we need a different approach for filtered counts
   */
  async getRecordCount(filters?: FilterDefinition[], searchTerm?: string): Promise<number> {
    try {
      // If no filters or search terms, use the simple count endpoint
      if (!filters?.length && !searchTerm) {
        // Smart URL construction for count endpoint
        const countUrl = this.baseUrl.includes('/api/dutylogs')
          ? this.baseUrl + '/count'
          : this.baseUrl.replace('/dutylog', '/dutylog/count');

        const response = await fetch(countUrl, {
          method: 'GET',
          credentials: 'include',
          headers: this.getHeaders(),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        const count = typeof result === 'number' ? result : result.count;

        //  log.debug('dutyLogService.getRecordCount (unfiltered):', count);
        return count;
      }

      // For filtered counts, make a minimal request to get the lastRow which gives us the total count
      const filterModel = this.convertFiltersToFilterModel(filters || [], searchTerm);

      const countRequest: DutyLogFetchRequest = {
        startRow: 0,
        endRow: 25, // Small page size - we only care about the lastRow response
        rowGroupCols: [],
        valueCols: [],
        pivotCols: [],
        pivotMode: false,
        groupKeys: [],
        filterModel: filterModel,
        sortModel: [] // No sorting needed for count
      };

      const response = await this.fetchEntries(countRequest);
      // The backend returns lastRow which represents the total count of filtered results
      const count = response.totalCount || response.rows.length;

      log.debug('dutyLogService.getRecordCount (filtered):', count, 'filters:', filters, 'search:', searchTerm);
      return count;
    } catch (error) {
      log.error('Error getting duty log count:', error);
      throw this.createServiceError('API_ERROR', 'Failed to get duty log count', error);
    }
  }

  /**
   * Fetch duty log entries with simple pagination (HandsOnTable style)
   */
  async fetchEntries(params: DutyLogFetchRequest): Promise<DutyLogApiResponse> {
    try {
      // Use POST request for grid operations, matching backend contract
      const response = await fetch(this.baseUrl, {
        credentials: 'include',
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const backendResponse = await response.json(); // This could be { data: [], total: 0 } or { rows: [], lastRow: 0 }
      // Adapt it to the DutyLogApiResponse format that the rest of the service expects
      const adaptedResponse: DutyLogApiResponse = {
        rows: backendResponse.rows || backendResponse.data || [],
        totalCount: backendResponse.lastRow || backendResponse.total || 0
      };
      return adaptedResponse;
    } catch (error) {
      log.error('Error fetching duty log entries:', error);
      throw this.createServiceError('API_ERROR', 'Failed to fetch duty log entries', error);
    }
  }

  /**
   * Standard explorer interface compliance - fetches data and total count
   */
  async fetchData(request: DataRequest): Promise<DataResponse<DutyLogEntry>> {
    log.debug('[DEBUG] dutyLogService.fetchData - full request:', JSON.stringify(request, null, 2));
    try {
      // If crosslinkSearch is the ONLY dimension active, use the dedicated
      // crosslink endpoint (it's a faster path). When search or column
      // filters are also active, fall through to the normal POST path so
      // the proxy can compose all three.
      const hasFilters = !!(request.filters && request.filters.length > 0);
      const hasSearch = !!(request.search && String(request.search).trim());
      const crosslinkOnly = request.crosslinkSearch
        && request.crosslinkSearch.term
        && request.crosslinkSearch.type
        && !hasFilters
        && !hasSearch;

      if (crosslinkOnly) {
        const params = new URLSearchParams({
          q: request.crosslinkSearch!.term,
          type: request.crosslinkSearch!.type,
          pageSize: String(request.pageSize || 25),
          page: String(request.page || 1)
        });
        const url = `${this.baseUrl}/crosslink?${params.toString()}`;
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: this.getHeaders(),
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const crosslinkJson = await response.json();
        // Defensive mapping for backend response
        const dataArr = Array.isArray(crosslinkJson.data) ? crosslinkJson.data : [];
        const pagination = crosslinkJson.pagination || {};
        const total = typeof pagination.total === 'number' ? pagination.total : dataArr.length;
        const page = request.page || 1;
        return {
          data: dataArr,
          totalCount: total,
          page: page
        };
      }

      // Otherwise, use normal backend fetch logic
      // Service adapter should convert search object to string before calling this method
      const searchTerm = request.search;

      // Convert DataRequest to backend request format
      const pageSize = request.pageSize || 25;
      const page = request.page || 1; // Use page as-is, no conversion needed
      const backendRequest: DutyLogFetchRequest = {
        startRow: (page - 1) * pageSize,
        endRow: page * pageSize,
        rowGroupCols: [],
        valueCols: [],
        pivotCols: [],
        pivotMode: false,
        groupKeys: [],
        filterModel: this.convertFiltersToFilterModel(request.filters as any ?? [], searchTerm),
        // Sort with logDate-desc as a guaranteed tiebreaker. Without it,
        // sorting on a column with many ties (e.g. logClass = "North Slave"
        // for everyone) produces a non-stable order and the most recent
        // entries don't stay at the top.
        sortModel: (() => {
          const userSorts = (request.sort ?? []).map((sort: SortParam) => ({
            colId: sort.field,
            sort: sort.direction,
          }));
          const hasLogDate = userSorts.some((s) => s.colId === 'logDate');
          return hasLogDate
            ? userSorts
            : [...userSorts, { colId: 'logDate', sort: 'desc' as const }];
        })(),
        crosslink: request.crosslinkSearch ? true : undefined,
        crosslinkTerm: request.crosslinkSearch?.term,
        crosslinkType: request.crosslinkSearch?.type
      };

      log.debug('[DEBUG] dutyLogService.fetchData - search object:', request.search);
      log.debug('[DEBUG] dutyLogService.fetchData - search type:', typeof request.search);
      log.debug('[DEBUG] dutyLogService.fetchData - extracted term:', searchTerm);

      // When crosslink is composed with search/filters, the proxy returns the
      // post-filtered total directly. Trust that. For plain filter/search
      // requests, getRecordCount also works but doesn't know about crosslink,
      // so prefer the proxy's total whenever it's a real number.
      const dataResponse = await this.fetchEntries(backendRequest);
      const proxyTotal = dataResponse.totalCount;
      const totalCount = (typeof proxyTotal === 'number' && proxyTotal > 0)
        ? proxyTotal
        : await this.getRecordCount(request.filters as any, searchTerm);

      const result = {
        data: dataResponse.rows,
        totalCount: totalCount,
        page: request.page || 1
      };

      // log.debug('[DEBUG] dutyLogService.fetchData - returning result:', JSON.stringify(result, null, 2));
      return result;
    } catch (error) {
      log.error('Error in fetchData:', error);
      throw this.createServiceError('API_ERROR', 'Failed to fetch data', error);
    }
  }


  /**
   * Export duty log data to various formats
   */
  async exportData(format: 'csv' | 'excel' | 'pdf' | 'legal-pdf', filters?: FilterDefinition[], search?: string): Promise<Blob> {
    try {
      // For now, fetch all data and convert to requested format. The active
      // search term must be threaded through (#624) — fetchData applies
      // request.search via convertFiltersToFilterModel, so without it the
      // export ignored the search bar and re-fetched everything.
      const request: DataRequest = {
        page: 0,
        pageSize: 10000, // Large page size to get all data
        sort: [{ field: 'logDate', direction: 'desc' }],
        filters: filters || [],
        search: search?.trim() ? search.trim() : undefined,
      };

      const response = await this.fetchData(request as any);

      switch (format) {
        case 'csv':
          return this.exportToCsv(response.data);
        case 'excel':
          return this.exportToExcel(response.data);
        case 'pdf':
          return this.exportToPdf(response.data);
        case 'legal-pdf':
          return this.exportToLegalPdf(response.data);
        default:
          throw new Error(`Unsupported export format: ${format}`);
      }
    } catch (error) {
      log.error('Error exporting data:', error);
      throw this.createServiceError('API_ERROR', 'Failed to export data', error);
    }
  }

  /**
   * Get tracked test entry IDs for cleanup
   */
  getTestEntryIds(): string[] {
    return Array.from(this.testEntryIds);
  }

  /**
   * Clear a specific test entry ID from tracking
   */
  removeTestEntryId(id: string): void {
    this.testEntryIds.delete(id);
    this.saveTestEntryIds();
    log.debug(`🧹 Removed test entry ID from tracking: ${id}`);
  }

  /**
   * Clear all tracked test entry IDs
   */
  clearAllTestEntryIds(): void {
    const count = this.testEntryIds.size;
    this.testEntryIds.clear();
    this.saveTestEntryIds();
    log.debug(`🧹 Cleared all ${count} test entry IDs from tracking`);
  }

  /**
   * Log all tracked test entry IDs
   */
  logTestEntryIds(): void {
    const ids = Array.from(this.testEntryIds);
    log.debug(`🧪 TRACKED TEST ENTRY IDs (${ids.length}):`, ids);
  }

  // Private helper methods

  private convertFiltersToFilterModel(filters: FilterDefinition[], searchTerm?: string): Record<string, any> {
    const filterModel: Record<string, any> = {};

    // Add regular filters
    filters.forEach(filter => {
      switch (filter.operator) {
        case 'contains':
          filterModel[filter.field] = {
            filterType: 'text',
            type: 'contains',
            filter: filter.value
          };
          break;
        case 'equals':
          filterModel[filter.field] = {
            filterType: 'text',
            type: 'equals',
            filter: filter.value
          };
          break;
        case 'greaterThan':
          // Date range "from" — combine with existing lessThan if present
          if (filterModel[filter.field]?.type === 'inRange') {
            filterModel[filter.field].dateFrom = filter.value;
          } else if (filterModel[filter.field]?.type === 'lessThan') {
            filterModel[filter.field] = {
              filterType: 'date',
              type: 'inRange',
              dateFrom: filter.value,
              dateTo: filterModel[filter.field].dateTo || filterModel[filter.field].filter
            };
          } else {
            filterModel[filter.field] = {
              filterType: 'date',
              type: 'greaterThan',
              filter: filter.value
            };
          }
          break;
        case 'lessThan':
          // Date range "to" — combine with existing greaterThan if present
          if (filterModel[filter.field]?.type === 'inRange') {
            filterModel[filter.field].dateTo = filter.value;
          } else if (filterModel[filter.field]?.type === 'greaterThan') {
            filterModel[filter.field] = {
              filterType: 'date',
              type: 'inRange',
              dateFrom: filterModel[filter.field].filter,
              dateTo: filter.value
            };
          } else {
            filterModel[filter.field] = {
              filterType: 'date',
              type: 'lessThan',
              filter: filter.value
            };
          }
          break;
        default:
          filterModel[filter.field] = {
            filterType: 'text',
            type: 'contains',
            filter: filter.value
          };
      }
    });

    // Add search filter if provided
    if (searchTerm && searchTerm.trim()) {
      // For a general search, we can apply a filter across multiple fields.
      // The backend would need to handle this logic. A simple approach is
      // to have a special filter key like 'globalSearch'.
      filterModel['globalSearch'] = {
        filterType: 'text',
        type: 'contains',
        filter: searchTerm.trim()
      };
      log.debug('[DEBUG] dutyLogService.convertFiltersToFilterModel:', { searchTerm, filterModel });
    }

    return filterModel;
  }

  private async exportToCsv(data: DutyLogEntry[]): Promise<Blob> {
    const headers = ['Date/Time', 'User', 'Class', 'Context', 'Log Entry', 'Enhanced Content'];
    const csvRows = [
      headers.join(','),
      ...data.map(entry => [
        `"${entry.logDate}"`,
        `"${entry.logUser}"`,
        `"${entry.logClass}"`,
        `"${entry.logContext}"`,
        `"${entry.body.replace(/"/g, '""')}"`,
        `"${(entry.enhanced_body || '').replace(/"/g, '""')}"`
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  }

  private async exportToExcel(data: DutyLogEntry[]): Promise<Blob> {
    // For now, return CSV with Excel MIME type
    // In a real implementation, you'd use a library like xlsx
    const csvBlob = await this.exportToCsv(data);
    return new Blob([await csvBlob.arrayBuffer()], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  private async exportToPdf(data: DutyLogEntry[]): Promise<Blob> {
    const entries: LogbookEntry[] = data.map(entry => ({
        date: new Date(entry.logDate).toLocaleString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        }),
        author: `Duty Officer (${entry.logContext}-${entry.logClass}): ${entry.logUser}`,
        body: entry.body
    }));

    return generateLogbookPdf({
        title: 'Duty Log Report',
        entries,
        footer: DEFAULT_REPORT_FOOTER
    });
  }

  private async exportToLegalPdf(data: DutyLogEntry[]): Promise<Blob> {
    const entries: LogbookEntry[] = data.map(entry => ({
        date: new Date(entry.logDate).toLocaleString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        }),
        author: `Duty Officer (${entry.logContext}-${entry.logClass}): ${entry.logUser}`,
        body: entry.body,
        additionalContext: entry.enhanced_body || undefined
    }));

    return generateLogbookPdf({
        title: 'Duty Log Report (Legal)',
        entries,
        footer: DEFAULT_REPORT_FOOTER
    });
  }

  private createServiceError(
    type: 'VALIDATION_ERROR' | 'API_ERROR' | 'NETWORK_ERROR',
    message: string,
    originalError?: any,
    field?: string
  ): DutyLogServiceError {
    return {
      type,
      message,
      code: originalError?.code,
      details: originalError,
      field
    };
  }

  private saveTestEntryIds(): void {
    try {
      localStorage.setItem('dutylog_test_entry_ids', JSON.stringify(Array.from(this.testEntryIds)));
    } catch (error) {
      log.warn('Failed to save test entry IDs to localStorage:', error);
    }
  }

  private loadTestEntryIds(): void {
    try {
      const stored = localStorage.getItem('dutylog_test_entry_ids');
      if (stored) {
        const ids = JSON.parse(stored) as string[];
        this.testEntryIds = new Set(ids);
        log.debug(`📋 Loaded ${ids.length} tracked test entry IDs from localStorage`);
      }
    } catch (error) {
      log.warn('Failed to load test entry IDs from localStorage:', error);
    }
  }
}

// Lazy singleton - created on first access after config is loaded
let _dutyLogService: DutyLogService | null = null;

const getDutyLogService = (): DutyLogService => {
  if (!_dutyLogService) {
    const baseUrl = getBackendUrl();
    const finalUrl = `${baseUrl}/api/dutylogs`;
    log.debug('✅ getDutyLogService() - Creating service with URL:', finalUrl);
    _dutyLogService = new DutyLogService(finalUrl);
  }
  return _dutyLogService;
};

// Proxy for backward compatibility - lazily initializes on first use
export const dutyLogService = new Proxy({} as DutyLogService, {
  get(_, prop) {
    const service = getDutyLogService();
    const value = (service as any)[prop];
    // Bind methods to the service instance
    return typeof value === 'function' ? value.bind(service) : value;
  }
});

// Export utility functions for test cleanup
export const testUtils = {
  /**
   * Get all tracked test entry IDs
   */
  getTestEntryIds: () => dutyLogService.getTestEntryIds(),

  /**
   * Log all tracked test entry IDs to console
   */
  logTestEntryIds: () => dutyLogService.logTestEntryIds(),

  /**
   * Clear tracking for a specific test entry ID
   */
  removeTestEntryId: (id: string) => dutyLogService.removeTestEntryId(id),

  /**
   * Clear all tracked test entry IDs
   */
  clearAllTestEntryIds: () => dutyLogService.clearAllTestEntryIds()
};
